/* eslint-disable @typescript-eslint/no-explicit-any */
// Design v10 Project team page (reference shareVals additions): the search/invite box filters the list; tabs All, Team,
// Invited and Suggested with counts; members and invites are separate sections; Suggested lists people from your other
// projects (filtered as you type) with a one-click Invite, which uses the same invite flow as typing @username.
import type { Project, User } from '@/domain/model';
import type { SharingInfo } from './ports';
import { onInvite } from './v6share';
import { t as tr } from '@/app/i18n';

type C = any;
type VM = Record<string, any>;

/** Search text of the invite box (lowercase, leading @ dropped). */
export const teamQuery = (c: C): string => (c.state.inviteDraft || '').trim().toLowerCase().replace(/^@/, '');
/** Whether a person matches the search text (name, username or e-mail). */
export const teamMatch = (q: string, u: { name?: string; username?: string | null; email?: string | null }): boolean => !q || [u.name, u.username, u.email].some(v => (v || '').toLowerCase().includes(q));

/** User ids already invited to the project (demo: on the project; production: the server's pending invitations). */
function invitedIds(c: C, proj: Project): Set<string> {
  if (c.repo.demo) return new Set((proj.invites || []).map(v => v.u));
  const sh = ((c.state.sharing || {})[proj.id]) as SharingInfo | undefined;
  return new Set((sh ? sh.invitations : []).map(iv => iv.invitee?.id).filter((x): x is string => !!x));
}
function invitedCount(c: C, proj: Project): number {
  if (c.repo.demo) return (proj.invites || []).length;
  const sh = ((c.state.sharing || {})[proj.id]) as SharingInfo | undefined;
  return sh ? sh.invitations.length : 0;
}

export function teamPageVals(c: C, proj: Project): VM {
  const me = c.state.me, q = teamQuery(c), tab = c.state.teamTab || 'all', inv = invitedIds(c, proj), nInv = invitedCount(c, proj);
  const visible = (c.state.projects as Project[]).filter(p => !p.deletedAt && (p.members || []).includes(me) && p.id !== proj.id);
  const known = [...new Set(visible.flatMap(p => p.members || []))].filter(id => id !== me && !proj.members.includes(id) && !inv.has(id));
  const shared = (id: string) => (c.state.projects as Project[]).filter(p => !p.deletedAt && (p.members || []).includes(me) && (p.members || []).includes(id)).map(p => p.name);
  const list = known.map(id => c.user(id) as User & { bg: string; ini: string }).filter(u => teamMatch(q, u));
  const cnt: Record<string, number> = { all: proj.members.length + nInv + known.length, team: proj.members.length, invited: nInv, suggest: known.length };
  const acc = c.accV();
  const seg = (k: string, label: string) => ({ label: tr(label) + ' ' + cnt[k], on: tab === k, onClick: () => c.setState({ teamTab: k }), bg: tab === k ? acc : 'transparent', color: tab === k ? '#fff' : 'var(--n8f9096, #8f9096)' });
  const invite = (u: User) => onInvite(c, proj, { key: 'Enter', target: { value: u.username ? '@' + u.username : (u.email || '') } } as unknown as KeyboardEvent);
  return {
    teamTabs: [seg('all', 'All'), seg('team', 'Team'), ...(nInv ? [seg('invited', 'Invited')] : []), seg('suggest', 'Suggested')],
    teamView: tab === 'all' || tab === 'team', suggestView: tab === 'all' || tab === 'suggest',
    showInvited: nInv > 0 && (tab === 'all' || tab === 'invited'), teamOnlyCount: String(proj.members.length), invitedCount: String(nInv),
    hasSuggest: known.length > 0, suggestNone: known.length > 0 && list.length === 0, suggestCount: String(list.length),
    suggestTitle: q ? tr('Matches from your other projects') : tr('People from your other projects'),
    suggest: list.map(u => { const sp = shared(u.id); return { name: u.name, user: u.username ? '@' + u.username : '', meta: sp.length ? tr('In {names}', { names: sp.slice(0, 2).join(', ') + (sp.length > 2 ? ' +' + (sp.length - 2) : '') }) : '', av: u.bg, ini: u.ini,
      onPickMd: (e: Event) => e.preventDefault(), onPick: () => invite(u) }; }),
  };
}
